import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/ApiService';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();

  const itemsPerPage = 10;

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await apiService.get('/users');
      setUsers(data.users || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter(user =>
      [user.name, user.phone_number, user.email, user.city, user.state, user.user_type]
        .some(field => field && field.toString().toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [searchQuery, users]);

  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredUsers, currentPage]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name || '',
      email: user.email || '',
      city: user.city || '',
      state: user.state || '',
      user_type: user.user_type || 'user',
      date_of_birth: user.date_of_birth ? new Date(user.date_of_birth).toISOString().split('T')[0] : '',
      referral_code: user.referral_code || '',
      createdAt: user.createdAt || ''
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    try {
      await apiService.patch(`/users/${selectedUser.user_id}`, editFormData);
      await fetchUsers();
      setIsEditModalOpen(false);
    } catch (err) {
      alert(err.message || 'Failed to update user');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsProcessing(true);
    try {
      await apiService.delete(`/users/${selectedUser.user_id}`);
      await fetchUsers();
      setIsDeleteModalOpen(false);
    } catch (err) {
      alert(err.message || 'Failed to delete user');
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadExcel = () => {
    const headers = ['Name', 'Phone', 'Email', 'City', 'State', 'Type'];
    const csvContent = [
      headers.join(','),
      ...users.map(u => [
        u.name || 'N/A',
        u.phone_number || 'N/A',
        u.email || 'N/A',
        u.city || 'N/A',
        u.state || 'N/A',
        u.user_type || 'N/A'
      ].map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading && users.length === 0) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '100px' }}>
      <i className="fas fa-circle-notch fa-spin" style={{ fontSize: '30px', color: 'var(--primary)' }}></i>
    </div>
  );

  return (
    <div className="users-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '32px', gap: '20px', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontFamily: 'Outfit', fontSize: '24px', fontWeight: '800', margin: '0', color: 'var(--text-main)' }}>User Management</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '4px' }}>Directory of all registered vehicle owners and partners.</p>
        </div>
        <button className="btn btn-primary" onClick={downloadExcel}>
          <i className="fas fa-file-export"></i>
          <span>Export Directory</span>
        </button>
      </div>

      <div className="admin-card" style={{ padding: '0', overflow: 'hidden' }}>
        <div style={{ padding: '24px', borderBottom: '1px solid var(--border-light)' }}>

          <div style={{ position: 'relative' }}>
            <i className="fas fa-search" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }}></i>
            <input
              type="text"
              placeholder="Search by name, phone, email or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 12px 12px 48px',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-light)',
                fontSize: '14px',
                background: 'white',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-main)', textAlign: 'left' }}>
                <th style={tableHeaderStyle}>USER PROFILE</th>
                <th style={tableHeaderStyle}>CONTACT DETAILS</th>
                <th style={tableHeaderStyle}>GEOGRAPHY</th>
                <th style={tableHeaderStyle}>GARAGE</th>
                <th style={tableHeaderStyle}>TYPE</th>
                <th style={tableHeaderStyle}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.length > 0 ? paginatedUsers.map(user => (
                <tr key={user.user_id} style={{
                  borderBottom: '1px solid var(--border-light)',
                  transition: 'background 0.2s'
                }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-light)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <td style={tableCellStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px', height: '40px', borderRadius: '12px',
                        background: 'var(--grad-primary)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontWeight: '700', fontSize: '16px'
                      }}>
                        {(user.name || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: '700', color: 'var(--text-main)' }}>{user.name || 'Anonymous'}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-dim)', letterSpacing: '0.5px' }}>
                          ID: {user.user_id.slice(0, 8).toUpperCase()}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="fas fa-phone-alt" style={{ fontSize: '12px', color: 'var(--primary)' }}></i>
                        <span style={{ fontWeight: '600' }}>{user.phone_number}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--text-muted)' }}>
                        <i className="fas fa-envelope" style={{ fontSize: '11px' }}></i>
                        <span>{user.email || 'No email provided'}</span>
                      </div>
                    </div>
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <i className="fas fa-map-marker-alt" style={{ color: 'var(--error)', fontSize: '12px' }}></i>
                      <span>{user.city ? `${user.city}, ${user.state || ''}` : <span style={{ color: 'var(--text-dim)' }}>Not Set</span>}</span>
                    </div>
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span style={{ fontWeight: '800', color: 'var(--primary)', fontSize: '15px' }}>{user.vehicle_count || 0}</span>
                      <button 
                        className="btn btn-outline" 
                        style={{ padding: '4px 8px', fontSize: '10px' }} 
                        onClick={() => navigate(`/vehicles?userId=${user.user_id}`)}
                      >
                        View Garage
                      </button>
                    </div>
                  </td>
                  <td style={tableCellStyle}>
                    <span className={`status-badge status-${user.user_type === 'partner' ? 'in_progress' : 'completed'}`} style={{ textTransform: 'capitalize' }}>
                      {user.user_type}
                    </span>
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn btn-outline" style={{ padding: '8px', minWidth: '36px' }} onClick={() => handleEditClick(user)} title="Edit">
                        <i className="fas fa-edit" style={{ fontSize: '13px' }}></i>
                      </button>
                      <button className="btn btn-outline" style={{ padding: '8px', minWidth: '36px', color: 'var(--error)' }} onClick={() => handleDeleteClick(user)} title="Delete">
                        <i className="fas fa-trash-alt" style={{ fontSize: '13px' }}></i>
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" style={{ padding: '64px', textAlign: 'center', color: 'var(--text-dim)' }}>
                    <i className="fas fa-search" style={{ fontSize: '48px', display: 'block', marginBottom: '16px', opacity: 0.1 }}></i>
                    No users found matching your search
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ padding: '24px', borderTop: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-main)' }}>
          <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: '600' }}>
            Showing {paginatedUsers.length} of {filteredUsers.length} entries
          </span>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              className="btn btn-outline"
              style={{ padding: '8px 16px' }}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
            >
              <i className="fas fa-chevron-left" style={{ marginRight: '8px', fontSize: '10px' }}></i> Previous
            </button>
            <button
              className="btn btn-outline"
              style={{ padding: '8px 16px' }}
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
            >
              Next <i className="fas fa-chevron-right" style={{ marginLeft: '8px', fontSize: '10px' }}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="modal-overlay">
          <div className="admin-card" style={{ maxWidth: '500px', width: '90%', padding: '0', overflow: 'hidden', border: 'none' }}>
            <div style={{ padding: '24px', background: 'var(--grad-primary)', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '800' }}>Update User Info</h3>
                <p style={{ margin: '4px 0 0', opacity: 0.8, fontSize: '12px' }}>Modifying profile for {selectedUser?.name || 'Anonymous'}.</p>
              </div>
              <button style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setIsEditModalOpen(false)}>×</button>
            </div>

            <form onSubmit={handleUpdateUser} style={{ padding: '24px' }}>
              <div className="grid-responsive">
                <div className="form-group col-12">
                  <label style={labelStyle}>Full Name</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={editFormData.name}
                    onChange={e => setEditFormData({ ...editFormData, name: e.target.value })}
                  />
                </div>
                <div className="form-group col-12">
                  <label style={labelStyle}>Email Address</label>
                  <input
                    type="email"
                    style={inputStyle}
                    value={editFormData.email}
                    onChange={e => setEditFormData({ ...editFormData, email: e.target.value })}
                  />
                </div>
                <div className="form-group col-6 col-md-12">
                  <label style={labelStyle}>City</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={editFormData.city}
                    onChange={e => setEditFormData({ ...editFormData, city: e.target.value })}
                  />
                </div>
                <div className="form-group col-6 col-md-12">
                  <label style={labelStyle}>Date of Birth</label>
                  <input
                    type="date"
                    style={inputStyle}
                    value={editFormData.date_of_birth}
                    onChange={e => setEditFormData({ ...editFormData, date_of_birth: e.target.value })}
                  />
                </div>
                <div className="form-group col-6 col-md-12">
                  <label style={labelStyle}>Referral Code</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={editFormData.referral_code}
                    disabled
                  />
                </div>
                <div className="form-group col-6 col-md-12">
                  <label style={labelStyle}>Registration Date</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={editFormData.createdAt ? new Date(editFormData.createdAt).toLocaleDateString('en-GB') : 'N/A'}
                    disabled
                  />
                </div>
                <div className="form-group col-12">
                  <label style={labelStyle}>User Role</label>
                  <select
                    style={inputStyle}
                    value={editFormData.user_type}
                    onChange={e => setEditFormData({ ...editFormData, user_type: e.target.value })}
                  >
                    <option value="user">Standard User</option>
                    <option value="admin">Administrator</option>
                    <option value="partner">RTO Partner</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '32px' }}>
                <button type="button" className="btn btn-outline" onClick={() => setIsEditModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={isProcessing}>
                  {isProcessing ? 'Saving changes...' : 'Save Profile'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {isDeleteModalOpen && (
        <div className="modal-overlay">
          <div className="admin-card" style={{ maxWidth: '420px', padding: '32px', textAlign: 'center' }}>
            <div style={{ width: '64px', height: '64px', background: 'var(--error-light)', color: 'var(--error)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '24px' }}>
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h3 style={{ fontSize: '20px', fontWeight: '800', margin: '0 0 12px' }}>Are you absolutely sure?</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px', lineHeight: '1.6', margin: '0 0 32px' }}>
              This will deactivate <strong>{selectedUser?.name || selectedUser?.phone_number}</strong>. This action can be reversed by an administrator later.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button className="btn btn-primary" style={{ background: 'var(--error)', border: 'none' }} onClick={handleDeleteConfirm} disabled={isProcessing}>
                {isProcessing ? 'Deleting...' : 'Confirm Deactivation'}
              </button>
              <button className="btn btn-outline" onClick={() => setIsDeleteModalOpen(false)}>Keep User Active</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.7); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 2000; padding: 20px; }
      `}</style>
    </div>
  );
};

const tableHeaderStyle = {
  padding: '16px 24px',
  color: 'var(--text-muted)',
  fontSize: '11px',
  fontWeight: '800',
  letterSpacing: '1px',
  borderBottom: '1px solid var(--border-light)'
};

const tableCellStyle = {
  padding: '20px 24px',
  fontSize: '14px',
  color: 'var(--text-main)'
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  fontWeight: '600',
  fontSize: '13px',
  color: 'var(--text-main)'
};

const inputStyle = {
  width: '100%',
  padding: '12px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-light)',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
  background: '#fcfcfc',
  transition: 'all 0.2s'
};

export default Users;